import React from 'react'
import { formatDiscountPercent, shouldShowDiscountBadge } from '../../../utils/cartUtils'
import s from './ProductMarkers.module.css'

interface ProductMarkersProps {
  tags: Array<{ id: number; name: string }> | string[]
  className?: string
  relative?: boolean
  discount?: number | null
}

export function DiscountBadge({
  discount,
  className,
}: {
  discount?: number | null
  className?: string
}) {
  if (!shouldShowDiscountBadge(discount)) return null

  return (
    <span className={`${s.discountBadge} ${className || ''}`}>
      {discountIcon()} -{formatDiscountPercent(discount)}%
    </span>
  )
}

export default function ProductMarkers({ tags, className, relative, discount }: ProductMarkersProps) {
  // Перевіряємо, чи tags є валідним масивом
  if (!tags || !Array.isArray(tags)) {
    // ProductMarkers: tags is not a valid array
    return null
  }

  // Перетворюємо tags в спільний тип для роботи з filter
  const tagsArray = tags as Array<{ id: number; name: string } | string>

  // Перевіряємо, чи всі теги мають валідну структуру
  const validTags = tagsArray.filter((tag): tag is { id: number; name: string } | string => {
    if (typeof tag === 'string') return true
    if (tag && typeof tag === 'object' && 'name' in tag) {
      return true
    }
    return false
  })

  if (validTags.length !== tagsArray.length) {
    // ProductMarkers: some tags have invalid structure
  }

  // Якщо немає валідних тегів, не рендеримо компонент
  const shouldShowDiscount = shouldShowDiscountBadge(discount)

  if (validTags.length === 0 && !shouldShowDiscount) {
    return null
  }

  // Функції для перевірки чи потрібно показувати мітки
  const hasTag = (tagName: string) =>
    tagsArray.some(tag => {
      if (typeof tag === 'string') {
        return tag === tagName
      } else if (tag && typeof tag === 'object' && 'name' in tag) {
        return tag.name === tagName
      }
      return false
    })
  const shouldShowBestseller = () => hasTag('bestseller')

  return (
    <div className={`${s.marks} ${className} ${relative ? s.relative : ''}`}>
      {hasTag('новинка') && <span className={s.new}>новинка</span>}
      {shouldShowBestseller() && (
        <span className={s.bestseller}>{bestsellerIcon()} bestseller</span>
      )}
      {shouldShowDiscount && <DiscountBadge discount={discount} className={s.discount} />}

      {/* Відображення всіх тегів */}
      {validTags && validTags.length > 0 && (
        <div className={s.allTags}>
          {validTags.map((tag, index: number) => {
            let tagName: string

            if (typeof tag === 'string') {
              tagName = tag
            } else if (tag && typeof tag === 'object' && 'name' in tag) {
              tagName = tag.name
            } else {
              // Invalid tag format
              return null
            }

            // Пропускаємо спеціальні теги, які вже показані вище
            if (['новинка', 'bestseller', 'знижка'].includes(tagName)) {
              return null
            }
            return (
              <span key={index} className={s.tag}>
                {tagName}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

const bestsellerIcon = () => {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.4744 7.02577C10.3369 5.23611 9.50365 4.11460 8.76855 3.12493C8.08788 2.20871 7.5 1.41751 7.5 0.250367C7.5 0.156617 7.44750 0.0709297 7.36425 0.0279687C7.28074 -0.0152500 7.18041 -0.0081719 7.10449 0.0470000C6.00049 0.836984 5.07935 2.16845 4.75758 3.43885C4.53420 4.32331 4.50464 5.31763 4.50049 5.97432C3.48096 5.75656 3.25001 4.23153 3.24757 4.21491C3.23609 4.13581 3.18776 4.06698 3.11745 4.02938C3.04641 3.99228 2.96290 3.98959 2.89088 4.02523C2.83741 4.05111 1.57861 4.69072 1.50537 7.24449C1.50023 7.32945 1.50000 7.41467 1.50000 7.49987C1.50000 9.98089 3.51879 11.9996 6.00000 11.9996C6.00342 11.9998 6.00708 12.0003 6.01001 11.9996C6.01099 11.9996 6.01195 11.9996 6.01317 11.9996C8.48829 11.9925 10.5000 9.97651 10.5000 7.49987C10.5000 7.37511 10.4744 7.02577 10.4744 7.02577ZM6.00000 11.4996C5.17284 11.4996 4.50000 10.7829 4.50000 9.90181C4.50000 9.87179 4.49977 9.84151 4.50195 9.80441C4.51195 9.43285 4.58252 9.17921 4.65991 9.01051C4.80495 9.32202 5.06421 9.60837 5.48536 9.60837C5.62355 9.60837 5.73537 9.49656 5.73537 9.35839C5.73537 9.00247 5.74270 8.59184 5.83132 8.22125C5.91019 7.89266 6.09865 7.54306 6.33743 7.26282C6.44362 7.62657 6.65067 7.92097 6.85282 8.20831C7.14213 8.61941 7.44120 9.04445 7.49370 9.76925C7.49686 9.81221 7.50005 9.85543 7.50005 9.90181C7.50000 10.7828 6.82716 11.4996 6.00000 11.4996Z"
        fill="#017223"
      />
    </svg>
  )
}

const discountIcon = () => {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.78701 1.11811C7.08462 0.994932 7.52724 0.823537 7.96694 0.996549C8.02755 1.02040 8.08761 1.04531 8.14711 1.07127C8.57988 1.26007 8.77161 1.69395 8.89496 1.99137C8.91702 2.04454 8.93715 2.09451 8.95638 2.14225C8.99939 2.24900 9.03792 2.34462 9.08330 2.43978C9.14664 2.57257 9.20659 2.66600 9.27003 2.72944C9.33347 2.79288 9.42690 2.85283 9.55970 2.91616C9.65488 2.96156 9.75047 3.00006 9.85726 3.04308C9.90496 3.06230 9.95500 3.08245 10.0081 3.10449C10.3056 3.22783 10.7395 3.41954 10.9283 3.85231C10.9543 3.91190 10.9793 3.97206 11.0031 4.03275C11.1762 4.47246 11.0048 4.91508 10.8816 5.21268C10.8596 5.26589 10.8384 5.31547 10.8183 5.36283C10.7732 5.46872 10.7327 5.56369 10.6975 5.66308C10.6484 5.80181 10.6247 5.91030 10.6247 6.00003C10.6247 6.08976 10.6484 6.19825 10.6975 6.33698C10.7327 6.43638 10.7731 6.53117 10.8182 6.63706C10.8384 6.68441 10.8596 6.73419 10.8816 6.78738C11.0048 7.08499 11.1762 7.52761 11.0031 7.96731C10.9793 8.02784 10.9544 8.08784 10.9285 8.14728C10.7397 8.58012 10.3057 8.77184 10.0082 8.89519C9.95514 8.91721 9.90522 8.93732 9.85754 8.95652C9.75075 8.99954 9.65493 9.03814 9.55974 9.08353C9.42692 9.14687 9.33347 9.20683 9.27003 9.27027C9.20659 9.33371 9.14664 9.42716 9.08330 9.55998C9.03792 9.65514 8.99942 9.75071 8.95643 9.85746C8.93720 9.90521 8.91701 9.95532 8.89496 10.0085C8.77162 10.3060 8.57991 10.7400 8.14707 10.9288C8.08758 10.9548 8.02754 10.9797 7.96695 11.0035C7.52724 11.1765 7.08462 11.0051 6.78702 10.8820C6.73378 10.8599 6.68417 10.8388 6.63679 10.8186C6.53091 10.7735 6.43601 10.7331 6.33662 10.6979C6.19789 10.6487 6.08940 10.6250 5.99967 10.6250C5.90994 10.6250 5.80145 10.6487 5.66272 10.6979C5.56336 10.7331 5.46861 10.7734 5.36277 10.8185C5.31535 10.8387 5.26560 10.8599 5.21232 10.8820C4.91472 11.0051 4.47210 11.1765 4.03239 11.0035C3.97179 10.9797 3.91173 10.9548 3.85223 10.9288C3.41942 10.7400 3.22770 10.3060 3.10436 10.0086C3.08231 9.95539 3.06217 9.90541 3.04294 9.85767C2.99993 9.75088 2.96142 9.65528 2.91602 9.56009C2.85268 9.42728 2.79273 9.33384 2.72929 9.27040C2.66585 9.20696 2.57241 9.14700 2.43960 9.08367C2.34444 9.03829 2.24887 8.99979 2.14212 8.95678C2.09446 8.93758 2.04420 8.91734 1.99112 8.89533C1.69366 8.77198 1.25970 8.58026 1.07089 8.14745C1.04493 8.08796 1.02002 8.02791 0.996184 7.96731C0.823171 7.52761 0.994565 7.08499 1.11774 6.78738C1.13978 6.73413 1.16091 6.68451 1.18110 6.63711C1.22619 6.53123 1.26659 6.43637 1.30180 6.33698C1.35097 6.19825 1.37466 6.08976 1.37466 6.00003C1.37466 5.91030 1.35097 5.80181 1.30180 5.66307C1.26659 5.56368 1.22622 5.46889 1.18112 5.36301C1.16093 5.31559 1.13979 5.26595 1.11774 5.21268C0.994566 4.91507 0.823172 4.47245 0.996185 4.03275C1.02005 3.97210 1.04498 3.91200 1.07095 3.85246C1.25978 3.41967 1.69371 3.22796 1.99116 3.10461C2.04432 3.08256 2.09429 3.06244 2.14202 3.04321C2.24879 3.00019 2.34443 2.96167 2.43961 2.91628C2.57241 2.85294 2.66585 2.79299 2.72929 2.72955C2.79273 2.66611 2.85268 2.57267 2.91602 2.43987C2.96141 2.34470 2.99992 2.24911 3.04293 2.14234C3.06217 2.09460 3.08231 2.04461 3.10436 1.99143C3.22771 1.69399 3.41943 1.26007 3.85222 1.07126C3.91172 1.04531 3.97178 1.02040 4.03237 0.996554C4.47208 0.823541 4.91470 0.994934 5.21230 1.11811C5.26558 1.14016 5.31522 1.16130 5.36264 1.18149C5.46852 1.22658 5.56331 1.26695 5.66270 1.30217C5.80144 1.35133 5.90992 1.37503 5.99966 1.37503C6.08939 1.37503 6.19788 1.35133 6.33661 1.30217C6.43600 1.26695 6.53079 1.22658 6.63668 1.18149C6.68409 1.16130 6.73374 1.14016 6.78701 1.11811ZM4.61909 5.17892C4.77365 5.33347 5.02424 5.33347 5.17879 5.17892C5.33335 5.02436 5.33335 4.77377 5.17879 4.61921C5.02424 4.46466 4.77365 4.46466 4.61909 4.61921C4.46453 4.77377 4.46453 5.02436 4.61909 5.17892ZM6.82058 7.38038C6.97514 7.53494 7.22573 7.53494 7.38028 7.38038C7.53484 7.22582 7.53484 6.97524 7.38028 6.82068C7.22573 6.66612 6.97514 6.66612 6.82058 6.82068C6.66602 6.97524 6.66602 7.22582 6.82058 7.38038ZM7.38426 4.61516C7.53070 4.76161 7.53070 4.99904 7.38426 5.14549L5.14545 7.38430C4.99900 7.53074 4.76157 7.53074 4.61512 7.38430C4.46867 7.23785 4.46867 7.00041 4.61512 6.85397L6.85393 4.61516C7.00037 4.46871 7.23781 4.46871 7.38426 4.61516Z"
        fill="#FF8E02"
      />
    </svg>
  )
}
